<script setup lang="ts">
// Shared avatar - a circle with a Trove-served image or a derived
// initial. Replaces ~8 hand-rolled avatar blocks (nav user card, Home
// composer / threads / agents, Conversations rows, Studio bubbles). The
// `src` must be an internal (Trove) URL; on a load error it falls back
// to the initial so a dead URL never shows a broken image.
import { computed, ref, watch } from 'vue';
import { wsUrl } from '@/lib/workspace';

const props = withDefaults(defineProps<{
  name?: string | null;
  src?: string | null;
  initials?: string;        // explicit override (e.g. 'Q' for the Ask lane)
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | number;
  solid?: boolean;          // identity-mark tone (filled) vs the soft default
  square?: boolean;         // rounded square instead of a circle
}>(), { size: 'md', solid: false, square: false });

const SIZES: Record<string, number> = { xs: 18, sm: 24, md: 32, lg: 36, xl: 44 };
const px = computed(() => (typeof props.size === 'number' ? props.size : SIZES[props.size] ?? 32));

const label = computed(() => {
  if (props.initials) return props.initials;
  const n = (props.name ?? '').trim();
  if (!n) return '?';
  return n.split(/\s+/).map((p) => p[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();
});

// Reset the failed flag if the src changes (a new image deserves a try).
const failed = ref(false);
watch(() => props.src, () => { failed.value = false; });
const showImg = computed(() => !!props.src && !failed.value);
// Direct <img> loads can't send the X-Hq-Ws header, so carry the active
// workspace as a `?ws` hint - otherwise an avatar from a non-default workspace
// 404s (the download is scoped to the caller's active workspace).
const imgSrc = computed(() => wsUrl(props.src));
</script>

<template>
  <span
    class="avatar"
    :class="{ 'avatar--solid': solid, 'avatar--square': square }"
    :style="{ '--avatar-size': px + 'px' }"
    aria-hidden="true"
  >
    <img v-if="showImg" :src="imgSrc!" alt="" @error="failed = true" />
    <template v-else>{{ label }}</template>
  </span>
</template>

<style scoped lang="scss">
@layer surface {
  .avatar {
    flex-shrink: 0;
    display: inline-grid;
    place-items: center;
    width: var(--avatar-size);
    height: var(--avatar-size);
    border-radius: var(--radius-pill);
    overflow: hidden;
    background: var(--surface-sunken);
    color: var(--ink-muted);
    font-family: var(--font-sans);
    font-size: calc(var(--avatar-size) * 0.42);
    font-weight: 600;
    line-height: 1;
    user-select: none;

    img { width: 100%; height: 100%; object-fit: cover; }
  }
  .avatar--solid  { background: var(--ink-subtle); color: var(--surface-canvas); }
  .avatar--square { border-radius: var(--radius-input); }
}
</style>
